import { Loader } from "@googlemaps/js-api-loader"
import { MapLatLng, MapOptions, MapGeocoderStatus } from "@/types/Map"

export interface IMapService {
  initMap(domEl: HTMLElement, mapOptions: MapOptions): Promise<void>
  setMarker(position: MapLatLng | null): void
  setMapCenter(position: MapLatLng, zoom: number | null | undefined): void
  setMapOptions(mapOptions: MapOptions): void
  geocodeAddress(address: string): Promise<MapLatLng | undefined>
}

const defaultGmapOptions = {
  center: { lat: 53.826, lng: -2.422 }, // center of GB
  zoom: 7,
  clickableIcons: false,
  tilt: 0,
  gestureHandling: "auto",
  zoomControl: true,
  panControl: true,
  mapTypeControl: true,
  fullscreenControl: true,
  disableDefaultUI: true,
  rotateControl: true,
  mapTypeId: "roadmap",
  keyboardShortcuts: false,
  streetViewControl: true,
}

const disabledGmapControls = {
  gestureHandling: "none",
  zoomControl: false,
  panControl: false,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  disableDoubleClickZoom: true,
  rotateControl: false,
} as google.maps.MapOptions

export class MapService implements IMapService {
  private map: google.maps.Map | undefined
  private marker: google.maps.Marker | undefined
  private gmapOptions: google.maps.MapOptions | undefined
  private geocodeService: google.maps.Geocoder | undefined

  setMapOptions(mapOptions: MapOptions) {
    if (!this.map) {
      throw Error("Map not initialized")
    }
    this.gmapOptions = this.getGmapOptions(mapOptions)
  }

  async initMap(domEl: HTMLElement, mapOptions: MapOptions): Promise<void> {
    if (this.map) {
      throw Error("Map already initialized")
    }
    await this.loadApi()
    this.gmapOptions = this.getGmapOptions(mapOptions)
    this.map = new google.maps.Map(domEl, this.gmapOptions)
    return Promise.resolve()
  }

  destroyMap() {
    this.map = undefined
  }

  setMarker(position: MapLatLng | null): void {
    if (!this.map || !this.gmapOptions) {
      throw Error("Map not initialized")
    }

    // Add/move marker
    if (position) {
      if (this.marker) {
        this.marker.setPosition(position)
      } else {
        this.marker = new google.maps.Marker({ position, map: this.map })
      }

      this.setMapCenter(position, this.gmapOptions.zoom)
    }

    // Remove marker
    if (!position && this.marker) {
      this.marker.setMap(null)
      this.marker = undefined
      this.setMapCenter(defaultGmapOptions.center, defaultGmapOptions.zoom)
      return
    }
  }

  setMapCenter(
    position: MapLatLng,
    zoom: number | null | undefined = null,
  ): void {
    if (!this.map) {
      throw Error("Map not initialized")
    }
    this.map.panTo(position)
    if (zoom) this.map.setZoom(zoom)
  }

  async geocodeAddress(address: string): Promise<MapLatLng | undefined> {
    await this.loadApi()

    return new Promise<MapLatLng | undefined>((resolve, reject) => {
      if (this.geocodeService === undefined) {
        this.geocodeService = new google.maps.Geocoder()
      }

      this.geocodeService.geocode({ address }, (results, status) => {
        if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
          reject(MapGeocoderStatus.INVALID_ADDRESS)
          return
        }

        if (status !== google.maps.GeocoderStatus.OK || !results?.length) {
          reject(MapGeocoderStatus.ERROR)
          return
        }

        const { location } = results[0].geometry

        return resolve({
          lat: location.lat(),
          lng: location.lng(),
        })
      })
    })
  }

  private getGmapOptions(mapOptions: MapOptions) {
    return {
      ...defaultGmapOptions,
      ...(mapOptions.mapType ? { mapTypeId: mapOptions.mapType } : {}),
      ...(mapOptions.center ? { center: mapOptions.center } : {}),
      ...(mapOptions.zoom ? { zoom: mapOptions.zoom } : {}),
      ...(mapOptions.hasControls ? {} : disabledGmapControls),
    }
  }

  private async loadApi(): Promise<void> {
    const apiKey = import.meta.env.SO_GOOGLE_MAPS_API_KEY
    if (!apiKey) {
      throw Error("Missing Google Maps API key")
    }

    const apiLoader = new Loader({
      apiKey,
      version: "weekly",
      region: "GB",
    })

    // Skip if Gmap API is already loaded
    if (typeof google !== "undefined") {
      return Promise.resolve()
    }

    try {
      // Creates 'google.maps' as global var if it doesn't exist
      await apiLoader.load()
      return Promise.resolve()
    } catch (e) {
      throw Error(e as string)
    }
  }
}
